import React, { useCallback, useEffect, useState } from 'react';
import { Eye, Pencil, Search } from 'lucide-react';
import * as api from '../../api';
import { Btn, RNLLoader, Select, Table } from '../UI';
import Pagination from '../requisition/Pagination';
import { TakeoffStatusPill } from './MTOShared';
import { EDITABLE, TAKEOFF_STATUS, fmt, fmtDate } from './mtoUtils';

const MODES = {
  register:  { title: 'Take-Off Register', sub: 'The latest revision of every take-off across all projects.' },
  mine:      { title: 'My Take-Offs', sub: 'Take-offs you prepared — continue a draft or correct a returned one.' },
  revisions: { title: 'Revision History', sub: 'Every revision of every take-off — R0 → R1 → R2. An approved revision supersedes the one before it.' },
};

// One paged list behind the Register, My Take-Offs and Revision History tabs.
// It asks the server for a page at a time; searching runs only when Search is pressed.
export default function MTORegister({ mode = 'register', initialStatus = '', onOpen }) {
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState({ search: '', project_id: '', status: initialStatus });
  const [page, setPage] = useState({ skip: 0, limit: 25 });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { api.listProjects(0, 200).then(r => setProjects(r?.items || r || [])).catch(() => {}); }, []);
  useEffect(() => { setFilters(f => ({ ...f, status: initialStatus })); setPage(p => ({ ...p, skip: 0 })); }, [initialStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.listTakeoffs({
        ...filters, mine: mode === 'mine', all_revisions: mode === 'revisions', skip: page.skip, limit: page.limit,
      });
      setRows(data || []);
      setTotal(data?.totalCount ?? (data || []).length);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setError(e.message || 'Could not load take-offs.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, mode]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k) => (v) => { setFilters(f => ({ ...f, [k]: v })); setPage(p => ({ ...p, skip: 0 })); };
  const runSearch = (e) => { e.preventDefault(); setFilters(f => ({ ...f, search: q.trim() })); setPage(p => ({ ...p, skip: 0 })); };
  const clear = () => { setQ(''); setFilters({ search: '', project_id: '', status: '' }); setPage(p => ({ ...p, skip: 0 })); };

  const cols = [
    { key: 'takeoff_ref', label: 'Ref', nowrap: true, render: (v) => <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#0b318b' }}>{v}</span> },
    { key: 'revision', label: 'Rev', align: 'center' },
    { key: 'project_code', label: 'Project', render: (v, r) => <span title={r.project_name}>{v || '—'}</span> },
    { key: 'building_zone', label: 'Building / Zone', minWidth: 120 },
    { key: 'discipline_name', label: 'Discipline' },
    { key: 'systems', label: 'System', minWidth: 170, maxWidth: 240 },
    { key: 'prepared_by_name', label: 'Prepared By' },
    { key: 'date_prepared', label: 'Date', nowrap: true, render: fmtDate },
    { key: 'material_count', label: 'Materials', align: 'right' },
    { key: 'total_quantity', label: 'Total Qty', align: 'right', render: (v) => fmt(v) },
    { key: 'total_procurement_quantity', label: 'Total Proc', align: 'right', render: (v) => fmt(v) },
    { key: 'status', label: 'Status', render: (v, r) => (
      <div>
        <TakeoffStatusPill status={v} />
        {r.currently_with && v === 'pending_approval' && <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 3 }}>with {r.currently_with}</div>}
      </div>
    ) },
    { key: 'id', label: 'Actions', nowrap: true, render: (id, r) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <Btn variant="secondary" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => onOpen(id)} title="View / traceability">
          {EDITABLE.includes(r.status) && mode !== 'revisions' ? <><Pencil size={13} /> Open</> : <><Eye size={13} /> View</>}
        </Btn>
      </div>
    ) },
  ];

  const { title, sub } = MODES[mode] || MODES.register;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20 }}>{title}</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>{sub}</p>
        </div>
        <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{loading ? 'Loading…' : <>Showing <b>{rows.length}</b> of <b>{total}</b></>}</span>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
        <form onSubmit={runSearch} style={{ display: 'flex', gap: 8, flex: '1 1 320px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search ref / building / system / drawing…"
                   style={{ width: '100%', padding: '10px 12px 10px 33px', borderRadius: 11, border: '1px solid var(--border-light)', background: 'var(--bg-base)', color: 'var(--text-primary)', fontSize: 13.5, fontFamily: 'var(--font)' }} />
          </div>
          <Btn type="submit" disabled={loading}><Search size={14} /> Search</Btn>
        </form>
        <div style={{ width: 240 }}>
          <Select title="Filter by project" value={filters.project_id} onChange={setFilter('project_id')}
                  options={[{ value: '', label: 'All projects' }, ...projects.map(p => ({ value: p.id || p.project_id, label: `${p.project_code} — ${p.project_name}` }))]} />
        </div>
        <div style={{ width: 190 }}>
          <Select title="Filter by status" value={filters.status} onChange={setFilter('status')}
                  options={[{ value: '', label: 'All statuses' }, ...Object.entries(TAKEOFF_STATUS).map(([value, m]) => ({ value, label: m.label }))]} />
        </div>
        <Btn variant="secondary" onClick={clear}>Clear</Btn>
      </div>

      {error && <div style={{ padding: '10px 14px', borderRadius: 10, background: '#fee2e2', color: '#991b1b', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {loading ? <RNLLoader title="Loading take-offs..." subtitle="Fetching this page" /> : (
        <Table cols={cols} rows={rows} emptyMsg={filters.search || filters.project_id || filters.status ? 'No take-offs match these filters.' : 'No take-offs yet — start one from New Take-Off.'} />
      )}
      <div style={{ marginTop: 12 }}>
        <Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} />
      </div>
    </div>
  );
}
